import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";
import { voiceStore } from "./vestaStore";

const when = (d) => (d ? new Date(d).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "");

function Item({ n, go, unread }) {
  return (
    <button type="button" onClick={() => go(n)} data-testid={`vesta-notif-${n.id}`}
      className={`block w-full px-4 py-2.5 text-left transition-colors hover:bg-white/5 ${unread ? "" : "opacity-60"}`}>
      <div className="flex items-center gap-2 text-xs font-semibold text-white">{unread && <span className="h-1.5 w-1.5 rounded-full bg-[#C17F59]" />}{n.titolo}</div>
      <div className="mt-0.5 text-[11px] leading-snug text-neutral-400">{n.testo}</div>
      <div className="mt-0.5 text-[10px] text-neutral-600">{when(n.at)}</div>
    </button>
  );
}

function Group({ g, go, unread, open, toggle }) {
  if (g.count === 1) return <div className="border-b border-neutral-900"><Item n={g.items[0]} go={go} unread={unread.has(g.items[0].id)} /></div>;
  const nu = g.items.filter((i) => unread.has(i.id)).length;
  return (
    <div className="border-b border-neutral-900" data-testid={`vesta-notif-group-${g.key}`}>
      <button type="button" onClick={toggle} className={`flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-white/5 ${nu ? "" : "opacity-60"}`} data-testid={`vesta-notif-group-toggle-${g.key}`}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs font-semibold text-white">{nu > 0 && <span className="h-1.5 w-1.5 rounded-full bg-[#C17F59]" />}{g.titolo}
            <span className="rounded-full bg-neutral-800 px-1.5 text-[10px] text-neutral-300">{g.count}</span></div>
          <div className="mt-0.5 truncate text-[11px] text-neutral-400">{g.items[0].testo}</div>
          <div className="mt-0.5 text-[10px] text-neutral-600">{when(g.at)}</div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-neutral-500" /> : <ChevronDown className="h-4 w-4 text-neutral-500" />}
      </button>
      {open && <div className="border-t border-neutral-900 bg-black/30 pl-3">{g.items.map((n) => <Item key={n.id} n={n} go={go} unread={unread.has(n.id)} />)}</div>}
    </div>
  );
}

// Campanella Ai VESTA: avvisi raggruppati per tipo e giorno; aprendola diventano letti
export default function VestaNotifiche() {
  const nav = useNavigate();
  const [d, setD] = useState({ non_lette: 0, items: [], gruppi: [] });
  const [open, setOpen] = useState(false);
  const [exp, setExp] = useState(null);
  const [unread, setUnread] = useState(new Set());
  const box = useRef(null);
  const load = useCallback(() => api.get("/vesta/notifiche").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (!open) return undefined;
    const off = (e) => !box.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, [open]);
  const toggleOpen = () => {
    if (open) { setOpen(false); return; }
    const ids = (d.gruppi || []).flatMap((g) => g.items).filter((i) => !i.letta).map((i) => i.id);
    setUnread(new Set(ids)); setExp(null); setOpen(true);
    if (ids.length) { setD((x) => ({ ...x, non_lette: Math.max(0, x.non_lette - ids.length) })); api.post("/vesta/notifiche/lette", { ids }).then(load).catch(() => {}); }
  };
  const go = (n) => {
    setOpen(false);
    if (n.link) nav(n.link);
    if (n.prompt) setTimeout(() => voiceStore.open(n.prompt), 300);
  };
  return (
    <div className="relative" ref={box}>
      <button type="button" onClick={toggleOpen} title="Avvisi" data-testid="vesta-notif-btn"
        className="relative shrink-0 rounded-full border border-neutral-800 p-2 text-neutral-400 transition-colors duration-200 hover:border-[#C17F59] hover:text-[#C17F59]">
        <Bell className="h-4 w-4" />
        {d.non_lette > 0 && <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-[#C17F59] px-1 text-center text-[10px] font-bold leading-[18px] text-black" data-testid="vesta-notif-count">{d.non_lette}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-950 shadow-2xl" data-testid="vesta-notif-panel">
          <div className="border-b border-neutral-800 px-4 py-2.5">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#E8B48F]">Avvisi Vesta</span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {(d.gruppi || []).map((g) => <Group key={g.key} g={g} go={go} unread={unread} open={exp === g.key} toggle={() => setExp(exp === g.key ? null : g.key)} />)}
            {!(d.gruppi || []).length && <p className="px-4 py-6 text-center text-xs text-neutral-500" data-testid="vesta-notif-empty">Nessun avviso.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
